/**
 * DashboardSectionPages — IMP follow-up.
 *
 * "See more" buttons on the dashboard (capped at 6 items per section)
 * navigate to these dedicated full pages, which list every record of
 * that kind with the standard 25-per-page pagination.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePagination, SeeMoreButton } from './usePagination';
import { ArchiveButton } from './ArchiveControls';
import { formatDate, formatDateTime } from '../utils/dates';
import { formatCurrency } from '../utils/currency';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const headerStyle = {
  display: 'flex', justifyContent: 'space-between',
  alignItems: 'flex-start', marginBottom: 18, gap: 12,
};
const containerStyle = {
  padding: '1.5rem 2rem', maxWidth: 1400, margin: '0 auto',
};
const tableStyle = {
  width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem',
  background: '#fff', borderRadius: 12, overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
};
const thStyle = {
  textAlign: 'left', padding: '0.7rem 0.9rem',
  background: '#f8fafc', color: '#475569', fontWeight: 600,
  fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em',
  borderBottom: '1px solid #e2e8f0',
};
const tdStyle = {
  padding: '0.65rem 0.9rem', borderBottom: '1px solid #f1f5f9',
};
const backLink = {
  color: '#0073EA', textDecoration: 'none', fontSize: '0.85rem',
  display: 'inline-flex', alignItems: 'center', gap: 4,
};

function PageShell({ title, subtitle, count, children }) {
  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <div style={containerStyle}>
        <Link to="/" data-testid="back-to-dashboard" style={backLink}>
          &larr; Back to Dashboard
        </Link>
        <div style={{ ...headerStyle, marginTop: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', color: '#0f172a' }}>
              {title}
            </h1>
            {subtitle && (
              <p style={{ margin: '6px 0 0', color: '#64748b' }}>
                {subtitle}
              </p>
            )}
          </div>
          {count != null && (
            <div style={{ background: '#fff', borderRadius: 10,
                          padding: '0.7rem 1.1rem',
                          boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
              <div style={{ fontSize: '0.7rem', color: '#64748b',
                            textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total
              </div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#0f172a' }}>
                {count}
              </div>
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1) Leads — full list with 25/page.
// ---------------------------------------------------------------------------
export function LeadsFullPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/leads`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setRows(Array.isArray(d) ? d : (d.leads || [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  const pager = usePagination(rows, 25);

  return (
    <PageShell title="All Leads" count={rows.length}
               subtitle="Every prospective tenant, landlord enquiry and contractor enquiry captured by Estatel AI AI triage.">
      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ background: '#fff', padding: '3rem', borderRadius: 12,
                      textAlign: 'center', color: '#64748b' }}>
          No leads yet.
        </div>
      ) : (
        <>
          <table style={tableStyle} data-testid="leads-full-table">
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Contact</th>
                <th style={thStyle}>Property of interest</th>
                <th style={thStyle}>Source</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Received</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pager.visible.map(l => {
                const name = [l.first_name, l.last_name].filter(Boolean).join(' ') || 'Unknown Contact';
                return (
                  <tr key={l._id} data-testid={`leads-full-row-${l._id}`}>
                    <td style={{ ...tdStyle, fontWeight: 600, color: '#0f172a' }}>{name}</td>
                    <td style={tdStyle}>{l.contact_type || '—'}</td>
                    <td style={tdStyle}>{(l.matched_property && l.matched_property.address) || l.property_of_interest || '—'}</td>
                    <td style={tdStyle}>{l.source || '—'}</td>
                    <td style={tdStyle}>{l.status || 'new'}</td>
                    <td style={tdStyle}>{formatDate(l.created_at)}</td>
                    <td style={tdStyle}>
                      <Link to={`/leads/${l._id}`}
                            style={{ color: '#0073EA', textDecoration: 'none', fontWeight: 600 }}>
                        View
                      </Link>
                      <span style={{ marginLeft: 10 }}>
                        <ArchiveButton kind="lead" id={l._id} size="xs"
                                        testid={`leads-full-archive-${l._id}`}
                                        onArchived={() => setRows(prev => prev.filter(x => x._id !== l._id))} />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <SeeMoreButton hasMore={pager.hasMore} remaining={pager.remaining}
                         onClick={pager.showMore} testid="leads-full" />
        </>
      )}
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// 2) Arrears — full list with 25/page.
// ---------------------------------------------------------------------------
export function ArrearsFullPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/digest-summary`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setRows(d.arrears_details || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  const pager = usePagination(rows, 25);

  return (
    <PageShell title="Arrears Overview" count={rows.length}
               subtitle="Every tenancy currently in arrears, sorted by months behind.">
      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ background: '#fff', padding: '3rem', borderRadius: 12,
                      textAlign: 'center', color: '#64748b' }}>
          No arrears recorded — well done!
        </div>
      ) : (
        <>
          <table style={tableStyle} data-testid="arrears-full-table">
            <thead>
              <tr>
                <th style={thStyle}>Tenant</th>
                <th style={thStyle}>Property</th>
                <th style={thStyle}>Total arrears</th>
                <th style={thStyle}>Monthly rent</th>
                <th style={thStyle}>Months in arrears</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pager.visible.map((a, idx) => (
                <tr key={a.tenant_id || idx} data-testid={`arrears-full-row-${idx}`}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#0f172a' }}>
                    {a.tenant_name || '—'}
                  </td>
                  <td style={tdStyle}>
                    {a.property_id ? (
                      <Link to={`/properties/${a.property_id}`}
                            style={{ color: '#0073EA', textDecoration: 'none' }}>
                        {a.property_address || '—'}
                      </Link>
                    ) : (a.property_address || '—')}
                  </td>
                  <td style={{ ...tdStyle, color: '#b91c1c', fontWeight: 600 }}>
                    {formatCurrency(a.total_balance || a.total_arrears || 0)}
                  </td>
                  <td style={tdStyle}>{formatCurrency(a.rent_amount || a.monthly_rent || 0)}</td>
                  <td style={tdStyle}>{a.months_in_arrears || 0} months</td>
                  <td style={tdStyle}>
                    {a.tenant_id && (
                      <Link to={`/tenants/${a.tenant_id}`}
                            style={{ color: '#0073EA', textDecoration: 'none', fontWeight: 600 }}>
                        View tenant
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <SeeMoreButton hasMore={pager.hasMore} remaining={pager.remaining}
                         onClick={pager.showMore} testid="arrears-full" />
        </>
      )}
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// 3) Active Section 8 cases — full list with 25/page.
// ---------------------------------------------------------------------------
export function ActiveCasesFullPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/digest-summary`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setRows(d.active_cases || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  const pager = usePagination(rows, 25);

  return (
    <PageShell title="Active Section 8 Cases" count={rows.length}
               subtitle="Every Section 8 possession case currently in motion.">
      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ background: '#fff', padding: '3rem', borderRadius: 12,
                      textAlign: 'center', color: '#64748b' }}>
          No active Section 8 cases — everything is up to date.
        </div>
      ) : (
        <>
          <table style={tableStyle} data-testid="active-cases-full-table">
            <thead>
              <tr>
                <th style={thStyle}>Tenant</th>
                <th style={thStyle}>Property</th>
                <th style={thStyle}>Stage</th>
                <th style={thStyle}>Arrears</th>
                <th style={thStyle}>Next deadline</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pager.visible.map(c => (
                <tr key={c.id || c._id || c.case_id} data-testid={`active-cases-full-row-${c.id || c._id || c.case_id}`}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#0f172a' }}>
                    {c.tenant_name || '—'}
                  </td>
                  <td style={tdStyle}>{c.property_address || '—'}</td>
                  <td style={tdStyle}>Stage {c.stage || '?'}{c.stage_label ? ` — ${c.stage_label}` : ''}</td>
                  <td style={{ ...tdStyle, color: '#b91c1c' }}>
                    {formatCurrency(c.total_arrears || 0)}
                  </td>
                  <td style={tdStyle}>{c.next_deadline ? formatDate(c.next_deadline) : '—'}</td>
                  <td style={tdStyle}>
                    <Link to={`/section8/case/${c.id || c._id || c.case_id}`}
                          style={{ color: '#0073EA', textDecoration: 'none', fontWeight: 600 }}>
                      Open case
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <SeeMoreButton hasMore={pager.hasMore} remaining={pager.remaining}
                         onClick={pager.showMore} testid="active-cases-full" />
        </>
      )}
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// 4) Maintenance full page — re-uses the existing MaintenanceKanban
//    component which already supports 25-per-column pagination.
// ---------------------------------------------------------------------------
export { default as MaintenanceFullPage } from './MaintenanceFullPageWrapper';
